import type { JSX } from 'react';
import { useState } from 'react';
import { Alert, Badge, Button, Center, Group, Loader, Menu, Modal, Stack, Table, Text, TextInput } from '@mantine/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { ApiError, offersApi } from '../../api';
import { useCan } from '../../core/useMe';
import { downloadXlsx } from '../../export/xlsx';
import { OFFER_SNAPSHOT_REASONS, OFFER_STATUS, OFFER_TABS, formatDateTime, todayIso } from '../../format';
import { downloadLink, linkTo } from '../../ui/links';
import { SubMenu } from '../../ui/SubMenu';
import { offerSheets } from './offerSheets';

/** What the menu needs of an offer - a tile's summary or the full offer alike. */
export interface OfferRef {
  id: string;
  code: string;
  name: string;
  status: string;
}

type Dialog =
  | { kind: 'snapshots'; offer: OfferRef }
  | { kind: 'duplicate'; offer: OfferRef }
  | { kind: 'delete'; offer: OfferRef };

const message = (e: unknown, fallback: string): string => (e instanceof ApiError || e instanceof Error ? e.message : fallback);

/**
 * What can be done with an offer as a whole, like with a project: open it,
 * in a new tab or in a tab; snapshots; status; downloads; copy; delete, set
 * apart at the end. Shared by the right-click menu and the ⋯ button on each
 * tile and on the offer's own page.
 */
export function useOfferActions(onDeleted?: (id: string) => void): {
  items: (offer: OfferRef, close: () => void) => JSX.Element;
  dialogs: JSX.Element;
} {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const canWrite = useCan('offers.write');
  const [dialog, setDialog] = useState<Dialog | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const close = (): void => setDialog(null);
  const refresh = (id: string): Promise<unknown> => Promise.all([queryClient.invalidateQueries({ queryKey: ['offers'] }), queryClient.invalidateQueries({ queryKey: ['offer', id] })]);

  const items = (offer: OfferRef, done: () => void): JSX.Element => {
    const run = (action: () => void) => () => {
      done();
      action();
    };
    const home = `/angebote/${offer.id}`;
    const snapshot = (): void => {
      offersApi.takeSnapshot(offer.id, 'manual')
        .then((r) => setNotice(r.created ? `Snapshot von ${offer.code} angelegt.` : `${offer.code}: seit dem letzten Snapshot nichts geändert – kein neuer angelegt.`))
        .catch((e: unknown) => setNotice(message(e, 'Der Snapshot ist fehlgeschlagen.')));
    };
    const excel = (): void => {
      offersApi.get(offer.id)
        .then((o) => downloadXlsx(offerSheets(o), `Angebot ${o.code} ${todayIso()}.xlsx`))
        .catch((e: unknown) => setNotice(message(e, 'Die Excel-Datei ließ sich nicht erzeugen.')));
    };
    const setStatus = (status: string): void => {
      offersApi.get(offer.id)
        .then((o) => offersApi.update(offer.id, o.version, { status }))
        .then(() => refresh(offer.id))
        .catch((e: unknown) => setNotice(message(e, 'Der Status ließ sich nicht ändern.')));
    };
    return (
      <>
        <Menu.Label>{offer.code} · {offer.name.length > 34 ? `${offer.name.slice(0, 34)} …` : offer.name}</Menu.Label>
        <Menu.Item component="a" {...linkTo(navigate, home, done)}>Öffnen</Menu.Item>
        <Menu.Item component="a" href={home} target="_blank" rel="noopener" onClick={done}>In neuem Tab öffnen</Menu.Item>
        <SubMenu label="Reiter öffnen">
          {OFFER_TABS.map((t) => <Menu.Item key={t.path} component="a" {...linkTo(navigate, `${home}/${t.path}`, done)}>{t.label}</Menu.Item>)}
        </SubMenu>

        <Menu.Divider />
        <Menu.Label>Angebot</Menu.Label>
        {canWrite && <Menu.Item onClick={run(snapshot)}>Snapshot jetzt anlegen</Menu.Item>}
        <Menu.Item onClick={run(() => setDialog({ kind: 'snapshots', offer }))}>Snapshots …</Menu.Item>
        {canWrite && (
          <SubMenu label="Status">
            {Object.entries(OFFER_STATUS).map(([k, v]) => (
              <Menu.Item key={k} onClick={run(() => setStatus(k))} rightSection={offer.status === k ? '✓' : undefined}>{v.label}</Menu.Item>
            ))}
          </SubMenu>
        )}

        <Menu.Divider />
        <SubMenu label="Herunterladen">
          <Menu.Item onClick={run(excel)}>Angebot als Excel</Menu.Item>
          <Menu.Item component="a" {...downloadLink(offersApi.exportUrl(offer.id), done)}>Angebot als Datei (JSON)</Menu.Item>
          <Menu.Item component="a" {...linkTo(navigate, `${home}/dateien`, done)}>Unterlagen (Reiter Dateien) …</Menu.Item>
        </SubMenu>

        {canWrite && (
          <>
            <Menu.Divider />
            <Menu.Label>Kopieren</Menu.Label>
            <Menu.Item onClick={run(() => setDialog({ kind: 'duplicate', offer }))}>Duplizieren …</Menu.Item>
            <Menu.Divider />
            <Menu.Item color="red" onClick={run(() => setDialog({ kind: 'delete', offer }))}>Löschen …</Menu.Item>
          </>
        )}
      </>
    );
  };

  const dialogs = (
    <>
      {notice && (
        <Modal opened onClose={() => setNotice(null)} title="Hinweis" size="sm">
          <Text size="sm">{notice}</Text>
        </Modal>
      )}
      {dialog?.kind === 'snapshots' && (
        <SnapshotsModal offer={dialog.offer} canWrite={canWrite} onClose={close} onRestored={async () => {
          await refresh(dialog.offer.id);
          queryClient.setQueryData(['offer-restored', dialog.offer.id], Date.now());
        }} />
      )}
      {dialog?.kind === 'duplicate' && (
        <DuplicateModal offer={dialog.offer} onClose={close} onDone={async (id) => { close(); await refresh(id); navigate(`/angebote/${id}`); }} />
      )}
      {dialog?.kind === 'delete' && (
        <DeleteModal offer={dialog.offer} onClose={close} onDone={async () => {
          const id = dialog.offer.id;
          close();
          queryClient.removeQueries({ queryKey: ['offer', id] });
          await queryClient.invalidateQueries({ queryKey: ['offers'] });
          onDeleted?.(id);
        }} />
      )}
    </>
  );

  return { items, dialogs };
}

function SnapshotsModal({ offer, canWrite, onClose, onRestored }: { offer: OfferRef; canWrite: boolean; onClose: () => void; onRestored: () => Promise<unknown> }): JSX.Element {
  const queryClient = useQueryClient();
  const list = useQuery({ queryKey: ['offer-snapshots', offer.id], queryFn: () => offersApi.snapshots(offer.id) });
  const refetch = (): Promise<void> => queryClient.invalidateQueries({ queryKey: ['offer-snapshots', offer.id] });
  const [notes, setNotes] = useState<string[] | null>(null);
  const take = useMutation({ mutationFn: () => offersApi.takeSnapshot(offer.id, 'manual'), onSettled: refetch });
  const restore = useMutation({
    mutationFn: (id: string) => offersApi.restoreSnapshot(offer.id, id),
    onSuccess: async (r) => { setNotes(r.notes); await Promise.all([refetch(), onRestored()]); },
  });
  const remove = useMutation({ mutationFn: (id: string) => offersApi.removeSnapshot(offer.id, id), onSettled: refetch });
  const removeAll = useMutation({ mutationFn: () => offersApi.removeAllSnapshots(offer.id), onSettled: refetch });
  const error = take.error ?? restore.error ?? remove.error ?? removeAll.error;
  return (
    <Modal opened onClose={onClose} title={`Snapshots – ${offer.code} ${offer.name}`} size="xl">
      <Stack gap="sm">
        <Text size="sm" c="dimmed">
          PST hält den Stand eines Angebots fest, wenn es nach einer Änderung geöffnet wird, von Hand und vor jedem Wiederherstellen:
          Angaben, Texte, HOAI und alle Varianten. Unterlagen, Auswertungen und erzeugte Pakete gehören nicht dazu; sie bleiben ohnehin erhalten.
          Wiederherstellen legt vorher einen Snapshot des jetzigen Stands an – es lässt sich also wieder zurücknehmen.
        </Text>
        {canWrite && <Group><Button size="xs" variant="light" onClick={() => take.mutate()} loading={take.isPending}>Snapshot jetzt anlegen</Button>
          {take.data && !take.data.created && <Text size="xs" c="dimmed">Seit dem letzten Snapshot nichts geändert.</Text>}
          {(list.data?.length ?? 0) > 0 && <Button size="xs" variant="subtle" color="red" loading={removeAll.isPending} title="z. B. nachdem Angaben zu Personen aus dem Angebot entfernt wurden – frühere Stände enthalten sie noch"
            onClick={() => { if (window.confirm(`Alle ${list.data!.length} Snapshots von ${offer.code} löschen? Frühere Stände lassen sich danach nicht mehr wiederherstellen.`)) removeAll.mutate(); }}>Alle löschen</Button>}</Group>}
        {error && <Alert color="red" py={6}>{message(error, 'Das hat nicht geklappt.')}</Alert>}
        {notes && <Alert color="green" py={6} title="Wiederhergestellt">{notes.length ? notes.map((n) => <Text key={n} size="xs">{n}</Text>) : <Text size="xs">Der frühere Stand ist wieder da.</Text>}</Alert>}
        {list.isPending ? <Center h={80}><Loader size="sm" /></Center> : list.isError ? <Alert color="red">{message(list.error, 'Die Snapshots ließen sich nicht laden.')}</Alert> : list.data.length === 0 ? (
          <Text size="sm" c="dimmed">Noch keine Snapshots.</Text>
        ) : (
          <Table striped withTableBorder fz="sm">
            <Table.Thead><Table.Tr><Table.Th>Zeitpunkt</Table.Th><Table.Th>Anlass</Table.Th><Table.Th>von</Table.Th><Table.Th>Varianten</Table.Th><Table.Th /></Table.Tr></Table.Thead>
            <Table.Tbody>
              {list.data.map((s) => (
                <Table.Tr key={s.id}>
                  <Table.Td style={{ whiteSpace: 'nowrap' }}>{formatDateTime(s.createdAt)}</Table.Td>
                  <Table.Td><Badge size="xs" variant="light" color={s.reason === 'manual' ? 'brand' : 'gray'}>{OFFER_SNAPSHOT_REASONS[s.reason]}</Badge>{s.label && <Text size="xs" c="dimmed">{s.label}</Text>}</Table.Td>
                  <Table.Td>{s.createdBy ?? '—'}</Table.Td>
                  <Table.Td><Text size="xs">{s.variants.join(' · ')}</Text></Table.Td>
                  <Table.Td style={{ whiteSpace: 'nowrap' }}>
                    {canWrite && (
                      <Group gap={4} wrap="nowrap">
                        <Button size="compact-xs" variant="light" loading={restore.isPending && restore.variables === s.id}
                          onClick={() => { if (window.confirm(`${offer.code} auf den Stand vom ${formatDateTime(s.createdAt)} zurücksetzen? Der jetzige Stand wird vorher als Snapshot festgehalten.`)) restore.mutate(s.id); }}>Wiederherstellen</Button>
                        <Button size="compact-xs" variant="subtle" color="red" onClick={() => { if (window.confirm('Diesen Snapshot löschen?')) remove.mutate(s.id); }}>Löschen</Button>
                      </Group>
                    )}
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}
      </Stack>
    </Modal>
  );
}

function DuplicateModal({ offer, onClose, onDone }: { offer: OfferRef; onClose: () => void; onDone: (id: string) => Promise<void> }): JSX.Element {
  const [code, setCode] = useState(`${offer.code.slice(0, 5)}2`.slice(0, 6));
  const [name, setName] = useState(`${offer.name} (Kopie)`);
  const [notes, setNotes] = useState<{ id: string; notes: string[] } | null>(null);
  const run = useMutation({
    mutationFn: () => offersApi.duplicate(offer.id, { code, name: name.trim() }),
    onSuccess: async (r) => { if (r.notes.length) setNotes({ id: r.id, notes: r.notes }); else await onDone(r.id); },
  });
  const codeOk = /^[A-Z0-9]{2,6}$/.test(code);
  return (
    <Modal opened onClose={onClose} title={`${offer.code} duplizieren`} size="md">
      <Stack gap="sm">
        <Text size="sm" c="dimmed">Die Kopie übernimmt Angaben, Texte, HOAI und alle Varianten als neuer Entwurf. Unterlagen, Auswertungen, Pakete und die Verbindung zu einem Projekt bleiben beim Original.</Text>
        <Group grow align="flex-start">
          <TextInput label="Kürzel" maw={140} value={code} onChange={(e) => setCode(e.currentTarget.value.toUpperCase().replace(/[^A-Z0-9]/g, ''))} maxLength={6} error={code && !codeOk ? '2–6 Zeichen' : undefined} data-autofocus />
          <TextInput label="Vorhaben" value={name} onChange={(e) => setName(e.currentTarget.value)} />
        </Group>
        {run.isError && <Alert color="red" py={6}>{message(run.error, 'Das Duplizieren ist fehlgeschlagen.')}</Alert>}
        {notes && <Alert color="brand" py={6} title="Kopie angelegt">{notes.notes.map((n) => <Text key={n} size="xs">{n}</Text>)}</Alert>}
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>{notes ? 'Schließen' : 'Abbrechen'}</Button>
          {notes ? <Button onClick={() => void onDone(notes.id)}>Zur Kopie</Button>
            : <Button onClick={() => run.mutate()} loading={run.isPending} disabled={!codeOk || !name.trim()}>Duplizieren</Button>}
        </Group>
      </Stack>
    </Modal>
  );
}

function DeleteModal({ offer, onClose, onDone }: { offer: OfferRef; onClose: () => void; onDone: () => Promise<void> }): JSX.Element {
  const [typed, setTyped] = useState('');
  const run = useMutation({ mutationFn: () => offersApi.remove(offer.id), onSuccess: onDone });
  return (
    <Modal opened onClose={onClose} title={`${offer.code} löschen`} size="md">
      <Stack gap="sm">
        <Text size="sm">
          Das Angebot <b>{offer.code} {offer.name}</b> wird mit allen Varianten, Unterlagen, Auswertungen, erzeugten Paketen und Snapshots gelöscht.
          Das lässt sich nicht rückgängig machen. Ein Projekt, das daraus entstanden ist, bleibt; Beteiligte und Referenzen auch.
        </Text>
        <Text size="sm" c="dimmed">Wer es aufheben will, lädt es vorher als Datei (JSON) herunter.</Text>
        <TextInput label={`Zur Bestätigung das Kürzel ${offer.code} eingeben`} value={typed} onChange={(e) => setTyped(e.currentTarget.value.toUpperCase())} data-autofocus />
        {run.isError && <Alert color="red" py={6}>{message(run.error, 'Das Löschen ist fehlgeschlagen.')}</Alert>}
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>Abbrechen</Button>
          <Button color="red" onClick={() => run.mutate()} loading={run.isPending} disabled={typed !== offer.code}>Endgültig löschen</Button>
        </Group>
      </Stack>
    </Modal>
  );
}
